import { createHeadlessEditor } from "@lexical/headless";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import LexicalClickableLinkPlugin from "@lexical/react/LexicalClickableLinkPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { Box, Input, Stack } from "@mantine/core";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { $createParagraphNode, $createTextNode, $getRoot, $nodesOfType, EditorState } from "lexical";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { isMyTasksUrl } from "../../app/utils";
import { InlineImageNode } from "./nodes/InlineImageNode";
import { MentionNode } from "./nodes/mentionNode";
import DragDropPaste from "./plugins/DragDropPaste";
import InlineImagePlugin from "./plugins/InlineImagePlugin";
import NewMentionsPlugin from "./plugins/MentionPlugin";
import { ResetEditorPlugin } from "./plugins/ResetEditorPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import "./RichTextEditorStyles.css";
import ExampleTheme from "./theme";

const INITIAL_STATE =
  '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

function Placeholder({ empty }: { empty: boolean }) {
  // Mantine Textarea placeholder sits inside the field; mimic that with absolute text that
  // has the same padding offset as the editor. We'll position it via CSS class.
  return <div className="mc-rte-placeholder">{empty ? "" : "Bruk @ for å tagge noen"}</div>;
}

function isLexicalJson(text: string) {
  // Your original heuristic preserved; consider a more robust try/catch JSON parse later.
  const prefix = '{"root":{"children":[';
  return typeof text === "string" && text.startsWith(prefix);
}

/**
 * Converts plain text -> Lexical JSON string (or passes through if already Lexical JSON).
 * Improvement: avoid module-level headless editor singleton to prevent listener buildup.
 */
export async function plainTextToLexicalState(text: string): Promise<string> {
  if (isLexicalJson(text)) return text;

  const editor = createHeadlessEditor({
    namespace: "HeadlessLexicalSerializer",
    nodes: [LinkNode, AutoLinkNode, MentionNode, InlineImageNode, HeadingNode, ListNode, ListItemNode, QuoteNode],
    onError: console.error,
  });

  const editorState = await new Promise<EditorState>((resolve) => {
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      unregister(); // prevent leaks
      resolve(editorState);
    });

    editor.update(() => {
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(text ?? ""));
      $getRoot().clear().append(paragraph);
    });
  });

  return JSON.stringify(editorState);
}

export async function lexicalStateToPlainText(editorState: EditorState): Promise<string> {
  // Keep signature; state is a Lexical EditorState
  return editorState.read(() => $getRoot().getTextContent());
}

type RichTextEditorProps = {
  richTextTree?: string;
  onChange: (editorState: EditorState, plainText: string, hasInlineImages: boolean) => void;
  onBlur?: () => void;
  showToolBar?: boolean;
  handleMentions: (payload: string[]) => void;
  members?: IProjectMemberApiItem[];
  // Optional Mantine-like wrapper props you may want
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  withAsterisk?: boolean;
  // If you want Textarea sizing behavior
  minRows?: number;
  maxRows?: number;
};

const urlRegExp = new RegExp(
  /(?:http[s]?:\/\/.)?(?:www\.)?[-a-zA-Z0-9@%._\+~#=]{2,256}\.[a-z]{2,6}\b(?:[-a-zA-Z0-9@:%_\+.~#?&\/\/=]*)/,
);
export function validateUrl(url: string): boolean {
  const res = url === "https://" || urlRegExp.test(url);
  return res;
}

export type RichEditorHandle = {
  reset: () => void;
  setContent: (json: string) => void; // optional
};

export const RichTextEditor = forwardRef<RichEditorHandle, RichTextEditorProps>(
  (
    {
      richTextTree,
      onChange,
      onBlur,
      showToolBar = true,
      handleMentions,
      members,
      label,
      description,
      error,
      required,
      disabled,
      withAsterisk,
      minRows = 3,
      maxRows = 12,
    },
    ref,
  ) => {
    const editorConfig = useMemo(
      () => ({
        editable: !disabled,
        namespace: "MyEditor",
        editorState: richTextTree && richTextTree.length > 0 ? richTextTree : INITIAL_STATE,
        theme: ExampleTheme,
        onError(error: unknown) {
          throw error;
        },
        nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, AutoLinkNode, LinkNode, MentionNode, InlineImageNode],
      }),
      [disabled, richTextTree],
    );

    const handleUpdateContent = (editorState: EditorState) => {
      // Improvement: single read transaction instead of multiple editorState.read(...) calls
      const payload = editorState.read(() => {
        const plainText = $getRoot().getTextContent();
        const hasInlineImages = $nodesOfType(InlineImageNode)?.length > 0;
        return { plainText, hasInlineImages };
      });

      onChange(editorState, payload.plainText, payload.hasInlineImages);
    };

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget;

        if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
          return;
        }

        onBlur?.();
      },
      [onBlur],
    );

    // Approximate Textarea height: use CSS vars to map rows -> minHeight; you can tune line-height as needed.
    const minHeight = `calc(var(--mantine-font-size-sm, 14px) * 1.55 * ${minRows} + var(--mantine-spacing-xs, 8px) * 2)`;
    const maxHeight = `calc(var(--mantine-font-size-sm, 14px) * 1.55 * ${maxRows} + var(--mantine-spacing-xs, 8px) * 2)`;
    const resetFnRef = useRef<null | (() => void)>(null);

    const registerReset = useCallback((fn: () => void) => {
      resetFnRef.current = fn;
    }, []);

    useImperativeHandle(ref, () => ({
      reset: () => resetFnRef.current?.(),
      setContent: (json: string) => {
        // optional: implement if you want to load a new state
      },
    }));

    return (
      <LexicalComposer initialConfig={editorConfig}>
        <Input.Wrapper
          label={label}
          description={description}
          error={error}
          required={required}
          withAsterisk={withAsterisk}
        >
          <Stack gap="xs" onBlur={handleBlur}>
            {showToolBar && <ToolbarPlugin />}

            {/* This Box is the "field" that visually matches Mantine Textarea */}
            <Box
              className={[
                "mc-rte-field",
                "mantine-Input-wrapper",
                disabled ? "mc-rte-disabled" : "",
                error ? "mc-rte-invalid" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{
                minHeight,
                maxHeight,
                position: "relative",
              }}
            >
              <RichTextPlugin
                contentEditable={
                  <ContentEditable className="mantine-Input-input mantine-Textarea-input mc-rte-contentEditable" />
                }
                placeholder={<Placeholder empty={isMyTasksUrl()} />}
                ErrorBoundary={LexicalErrorBoundary}
              />

              {/* Plugins */}
              {members != null && <NewMentionsPlugin members={members} handleMentions={(e) => handleMentions(e)} />}
              <ListPlugin />
              <LinkPlugin validateUrl={validateUrl} />
              <LexicalClickableLinkPlugin newTab />
              <HistoryPlugin />
              <InlineImagePlugin />
              <DragDropPaste />
              <ResetEditorPlugin registerReset={registerReset} />
            </Box>

            <OnChangePlugin onChange={handleUpdateContent} />
          </Stack>
        </Input.Wrapper>
      </LexicalComposer>
    );
  },
);

/**
 * ParsePlainTextPlugin
 * Improvement: move editor mutation into useEffect to avoid side effects during render.
 */
function ParsePlainTextPlugin({ text }: { text?: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (text == null) return;

    if (isLexicalJson(text)) {
      const editorState = editor.parseEditorState(text);
      // queueMicrotask is fine, but run inside effect; requestAnimationFrame also works.
      queueMicrotask(() => editor.setEditorState(editorState));
      return;
    }

    editor.update(() => {
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(text));
      $getRoot().clear().append(paragraph);
    });
  }, [editor, text]);

  return null;
}

export const RichTextReader: React.FC<{ richTextTree?: string }> = ({ richTextTree }) => {
  const readerConfig = useMemo(
    () => ({
      editable: false,
      editorState: INITIAL_STATE,
      namespace: "MyEditorReader",
      theme: ExampleTheme,
      onError(error: unknown) {
        throw error;
      },
      nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, LinkNode, AutoLinkNode, MentionNode, InlineImageNode],
    }),
    [],
  );

  return (
    <LexicalComposer initialConfig={readerConfig}>
      <Box className="mc-rte-reader">
        <ParsePlainTextPlugin text={richTextTree} />
        <HistoryPlugin />
        <LinkPlugin />
        <RichTextPlugin
          contentEditable={<ContentEditable className="mc-rte-readerContent" />}
          placeholder={<Placeholder empty={isMyTasksUrl()} />}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <LexicalClickableLinkPlugin newTab />
        <LinkPlugin />
      </Box>
    </LexicalComposer>
  );
};

export default RichTextEditor;
